/**
 * The recursive-ACL sentences every surface shares (Transfers row, MCP answer, approval dialog, knowledge pack, docs) —
 * S8 Task 8.2. `aclJobSummary` never claims a path is unchanged once a batch may have run (finding S8-04: NOTES «S8:
 * live-probed facts» R4 (iii) / R12 — an aborted batch keeps being applied by the service). `aclRecursiveGuaranteeLines`
 * is the ONLY text describing what a recursive approval protects (finding S8-R2-01), written from R8 / R9 / A11.
 */
import type { StorageAclChange, TransferJob, TransferJobAcl } from '../../types/azureStorage';
import { modifyRecalculatedMaskScopes } from './azureStorageAclRecursiveResult';
import { hnsServiceFacts, type HnsServiceFacts } from './azureStorageHnsFacts';

const plural = (n: number, one: string, many: string): string => `${n} ${n === 1 ? one : many}`;

/** Failures Azure counted beyond the listed ones (E3): the summary, the omitted record and the failure list share it. */
export const aclUnlistedFailuresSentence = (count: number): string => `${count} more failed paths are not listed; fix the cause and restart the job to apply the change to them.`;
/** What a recursive job does with failures — the Propagate dialog, its typed confirmation and the Restart confirmation. */
export const ACL_FAILURES_LINE = 'Up to 100 failures are listed and can be retried; there is no undo.';
export const aclRootLabel = (acl: Pick<TransferJobAcl, 'target'>): string => (acl.target.path ? `${acl.target.fileSystem}/${acl.target.path}` : acl.target.fileSystem);

function counts(acl: TransferJobAcl, withFailed = true): string {
  const { directories, files, failed } = acl.confirmed;
  const changed = `${plural(directories, 'directory', 'directories')} and ${plural(files, 'file', 'files')} changed`;
  return withFailed ? `${changed}, ${failed} failed` : changed;
}

function tails(acl: TransferJobAcl, facts: HnsServiceFacts): string {
  const unresolved = acl.failures.filter((f) => f.retry?.state !== 'succeeded');
  const fixed = acl.failures.length - unresolved.length;
  return [
    !facts.failedDirectoryChildrenVisited && unresolved.some((f) => f.isDirectory) ? ' Paths under a failed directory may not have been reached; propagate again from that directory once it is fixed.' : '',
    fixed > 0 ? ` ${fixed} fixed by Retry.` : '',
    acl.failuresOmitted > 0 ? ` ${aclUnlistedFailuresSentence(acl.failuresOmitted)}` : '',
  ].join('');
}

/**
 * R11 (review fixes C #4, coordinator ruling): how many paths got the change, that the rest could not be confirmed, and
 * why — a last batch that changed nothing and ended the job is also how Azure ends a job it cannot continue.
 */
function unconfirmedSummary(acl: TransferJobAcl, root: string): string {
  const { directories, files, failed } = acl.confirmed;
  const applied = `The change was applied to ${plural(directories + files, 'path', 'paths')} (${plural(directories, 'directory', 'directories')} and ${plural(files, 'file', 'files')}); ${failed} failed.`;
  return `${applied} The rest of ${root} could not be confirmed: Azure ended the job after a batch that changed no path, which is also how it ends a job it cannot continue (for example when it cannot read ${root}), so Jam SQL cannot tell whether every path was reached. Remainder of ${root}: unknown. Check the permissions on ${root} and restart the job.`;
}

function stoppedSummary(prefix: string, acl: TransferJobAcl, root: string): string {
  const uncertain = acl.traversal.uncertain;
  if (uncertain) return `${prefix} Confirmed: ${counts(acl)}. The batch that was running (up to ${acl.batchSize} paths) may have been applied in part; Jam SQL cannot tell which of its paths changed. Remainder of ${root}: unknown.`;
  if (acl.traversal.phase === 'finished') return `${prefix} Every batch finished: ${counts(acl)}.`;
  return `${prefix} Confirmed: ${counts(acl)}. Remainder of ${root}: unknown — this job sent no request for it after the last confirmed batch.`;
}

/** One summary line (plus the omitted / retry tails) of an `acl` job; Resume / Restart hints are the surface's. */
export function aclJobSummary(job: Pick<TransferJob, 'state' | 'acl'>, facts: HnsServiceFacts = hnsServiceFacts()): string {
  const acl = job.acl;
  if (!acl) return '';
  const root = aclRootLabel(acl);
  const settled = job.state !== 'planning' && job.state !== 'queued' && job.state !== 'running' && job.state !== 'cancelling';
  if (settled && acl.epoch > 1 && acl.traversal.phase === 'not-started' && acl.traversal.batchesCommitted === 0 && !acl.traversal.uncertain && !acl.traversal.failure) {
    return 'Restart requested; nothing has been applied in this attempt yet.';
  }
  const code = acl.traversal.failure?.code;
  if (code === 'TargetChanged' && acl.traversal.batchesCommitted === 0 && !acl.traversal.uncertain) return `${root} changed after you confirmed; nothing was sent.`;
  if (code === 'TargetReplaced') return `${root} was replaced while the job ran; the batch sent before the check (up to ${acl.batchSize} paths) may have been applied to the new directory. Nothing else was sent. Confirmed before that: ${counts(acl)}.${tails(acl, facts)}`;
  if (code === 'AclTraversalUnconfirmed') return `${unconfirmedSummary(acl, root)}${tails(acl, facts)}`;
  switch (job.state) {
    case 'planning':
    case 'queued':
    case 'running':
    case 'cancelling':
      return `Applying access control to ${root}: ${counts(acl)} so far.`;
    case 'completed':
      return `Done: ${counts(acl, acl.confirmed.failed > 0)}.${tails(acl, facts)}`;
    case 'partial':
      return `Finished with failures: ${counts(acl)}.${tails(acl, facts)}`;
    case 'failed': {
      const reason = acl.traversal.failure?.message ?? 'the job stopped before it finished.';
      return `${stoppedSummary(`Failed: ${/[.!?]$/u.test(reason) ? reason : `${reason}.`}`, acl, root)}${tails(acl, facts)}`;
    }
    case 'cancelled':
      return `${stoppedSummary('Cancelled.', acl, root)}${tails(acl, facts)}`;
    case 'interrupted':
      return `${stoppedSummary('Interrupted.', acl, root)}${tails(acl, facts)}`;
  }
}

/** What proves a recursive job's root is still the same one between batches: a directory's creation time, or nothing for a file-system root (its HEAD is the container's). */
export type AclRootIdentity = 'creation-time' | 'none';
export const aclRootIdentity = (target: { path: string }): AclRootIdentity => (target.path === '' ? 'none' : 'creation-time');

function midJobLine(root: string, facts: HnsServiceFacts, noun: string, identity: AclRootIdentity): string {
  if (facts.creationTimeIdentity && identity === 'creation-time') return `Before each later batch Jam SQL checks that ${root} is still the same directory and stops if it was replaced; a replacement made after a check can still receive that one batch.`;
  if (facts.creationTimeIdentity) return `Before each later batch Jam SQL checks only that ${root} still exists: Azure reports no creation time for a file-system root, so if ${root} is deleted and re-created while the job runs, Jam SQL cannot detect it and later batches may be applied to the new file system.`;
  return facts.rootReplacedMidJob === 'refused'
    ? `If ${root} is replaced while the job runs, the next batch is refused by Azure and the job stops.`
    : `If ${root} is replaced while the job runs, later batches may be applied to the new ${noun}; Jam SQL cannot detect this.`;
}

/**
 * The approval / dialog / docs sentences for a recursive change of `root`. With `shortEtag` = a first run (the root's
 * version was read at approval); without it = the Restart variant (D14: a Restart confirms a path, not a version).
 * `rootIdentity` = the evidence that can prove the root was not replaced (review fixes C #3): a file-system root has
 * no creation time, so its text never promises that replacement is detected.
 */
export function aclRecursiveGuaranteeLines(input: { root: string; shortEtag?: string; facts?: HnsServiceFacts; rootIdentity?: AclRootIdentity }): string[] {
  const { root, shortEtag } = input;
  const facts = input.facts ?? hnsServiceFacts();
  const identity = input.rootIdentity ?? 'creation-time';
  const fileSystem = identity === 'none';
  const noun = fileSystem ? 'file system' : 'directory';
  const window = `That check cannot cover the moment between it and the first batch: Azure cannot make a recursive change conditional, so if ${root} is deleted and re-created in that moment, the change is applied to the new ${noun} and everything ${fileSystem ? 'in' : 'under'} it.`;
  const midJob = midJobLine(root, facts, noun, identity);
  const subtree = `Files and directories added, removed or changed under ${root} while the job runs get the change as they are when their batch runs.`;
  if (shortEtag === undefined) {
    const first = fileSystem
      ? `Jam SQL checks right before the first batch that ${root} still exists and stops without sending anything if it does not.`
      : `Jam SQL checks right before the first batch that ${root} is still a directory and stops without sending anything if it is not.`;
    return [first, window, midJob, subtree];
  }
  const checked = fileSystem ? `Checked now: the file system ${root} exists (version ${shortEtag}).` : `Checked now: ${root} is a directory (version ${shortEtag}).`;
  if (facts.recursiveAclHonoursIfMatch) {
    return [checked, `The first batch is sent on condition that ${root} still has this version; if it was replaced or changed, Azure refuses it and nothing is changed.`, midJob, subtree];
  }
  const recheck = fileSystem
    ? `Jam SQL checks it again right before the first batch and stops without sending anything if ${root} is gone or has another version.`
    : `Jam SQL checks it again right before the first batch and stops without sending anything if ${root} is gone, is no longer a directory or has another version.`;
  return [checked, recheck, window, midJob, subtree];
}

/**
 * A recursive merge that changes a scope's entries without stating its mask (row C7): Azure recalculates that mask on
 * every path it changes, so the approval and the Propagate dialog say so (the root preview shows the effect).
 */
export function aclModifyMaskLine(change: StorageAclChange, facts: Pick<HnsServiceFacts, 'recursiveModifyKeepsExplicitMask'> = hnsServiceFacts()): string | undefined {
  const scopes = modifyRecalculatedMaskScopes(change, facts);
  if (scopes.length === 0) return undefined;
  const which = scopes.length === 2 ? 'access and default masks' : `${scopes[0]} mask`;
  return `Azure recalculates the ${which} of every path this merge changes, because the merge does not set ${scopes.length === 2 ? 'them' : 'it'}: named entries and the owning group can gain permissions the old mask blocked.`;
}
